<template>
	<div class="base-modal" :class="{ 'base-modal-bottom': position === 'bottom' }">
		<header class="base-modal__header" v-if="hasHeader">
			<div class="base-modal-toolbar">
				<div v-if="title || subTitle" class="base-modal__header__title base-modal-title">
					<div style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden">
						{{ title }}
					</div>
					<div v-if="subTitle" class="base-modal__header__subtitle">
						{{ subTitle }}
					</div>
				</div>

				<div v-if="closable" class="base-modal__header__icons">
					<button @click="close">
						<FontAwesomeIcon :icon="faTimes" />
					</button>
				</div>

				<BasicButton
					v-if="hasDoneButton"
					:disabled="disableDoneButton"
					@click="close"
					class="base-modal__header__button"
					:filled="false"
				>
					<FontAwesomeIcon :icon="jwCheckIcon" />&nbsp;
					{{ $t('WEBAPP_DONE') }}
				</BasicButton>
			</div>
		</header>

		<section class="base-modal__body">
			<slot />
		</section>

		<footer v-if="$slots.footer" style="">
			<slot name="footer"></slot>
		</footer>
	</div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';

import BasicButton from '@/ui-components/BasicButton.vue';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { jwCheckIcon } from '@/plugins/global-fontawesome';

const props = withDefaults(
	defineProps<{
		title?: string;
		subTitle?: string;
		closable?: boolean;
		position?: 'default' | 'bottom';
		hasHeader?: boolean;
		hasDoneButton?: boolean;
		disableDoneButton?: boolean;
		onWillDismiss?: () => Promise<void>;
	}>(),
	{
		closable: true,
		position: 'default',
		hasHeader: true,
	}
);

const emit = defineEmits<{
	(e: 'closed'): void;
	(e: 'dismiss', data?: any): void;
}>();

onMounted(async () => {
	if (props.onWillDismiss != null) {
		await props.onWillDismiss();
		emit('closed');
	}
});

function close() {
	emit('dismiss');
	emit('closed');
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.base-modal {
	display: flex;
	flex-direction: column;
	$toolbar-height: 86px;
	$footer-height: 49px;

	.base-modal__header {
		padding: 24px;
	}

	.base-modal-toolbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		--min-height: #{$toolbar-height};
		--border-width: 0 !important;

		.base-modal-title {
			font-size: 20px;
			font-weight: 500;
			letter-spacing: 0.0125em;
			padding: 0 20px;
		}
	}

	section {
		height: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		scrollbar-gutter: stable;
	}

	&__header {
		&__subtitle {
			font-size: 14px;
			color: var(--jw-color-secondary-tint);
		}

		&__button,
		&__icons {
			&:enabled {
				span {
					color: $jw-color-lightblue;
				}
			}
		}
	}
}
</style>
